const en = {
  meta: {
    title: "Portfolio — Bilal Alqatrawi",
    description:
      "Front-End Developer specializing in React.js and Next.js — SaaS platforms, dashboards, and e-commerce with Tailwind CSS, MUI, and production-ready API integration.",
  },
  common: {
    logo: "Bilal",
    loading: "Loading experience…",
    viewDemo: "Live Site",
    viewCode: "Source",
    submit: "Send message",
    sending: "Sending…",
    language: "Language",
    theme: "Theme",
  },
  nav: {
    home: "Home",
    about: "About",
    skills: "Skills",
    expertise: "Expertise",
    experience: "Experience",
    projects: "Projects",
    contact: "Contact",
    menu: "Open menu",
    close: "Close menu",
  },
  hero: {
    badge: "Freelance Front-End Developer · Gaza, Palestine",
    greeting: "Hi, I'm",
    name: "Bilal Alqatrawi",
    rolePrefix: "I build",
    roles: [
      "SaaS platforms & dashboards",
      "e-commerce experiences",
      "high-performance React & Next.js UIs",
    ],
    subtitle:
      "Front-End Developer specializing in React.js and Next.js. I ship scalable SaaS platforms, dashboards, and e-commerce apps with Tailwind CSS and MUI,  clean architecture, strong state management, and seamless REST API integration.",
    ctaPrimary: "View projects",
    ctaSecondary: "Contact me",
    scroll: "Scroll",
  },
  about: {
    title: "About",
    kicker: "Background & focus",
    headline: "Production-ready front-ends for real products.",
    p1: "I'm a Software Engineering graduate from Al-Azhar Gaza University with hands-on training in Java (Top-tech Center, 2021), full-stack web development (Kuwaiti Training Center, 2021), and advanced front-end study through Udemy and Elzero Web School (React.js, Next.js, Redux, React Query, Tailwind CSS, and REST APIs, 2022–2025).",
    p2: "Front-End Engineer specializing in React.js and Next.js to build high-performance SaaS platforms, corporate sites, clinical dashboards, and e-commerce apps. Expert in converting complex business requirements into scalable UI architectures, optimizing core web vitals, and establishing seamless RESTful API integrations. Dedicated to delivering accessible, pixel-perfect, and highly maintainable production-ready web solutions.",
    highlights: [
      { title: "2025 – Present", desc: "Freelance Front-End Developer" },
      {
        title: "B.Sc.",
        desc: "Software Engineering · Al-Azhar Gaza University",
      },
      {
        title: "React & Next.js",
        desc: "SaaS · dashboards · e-commerce · healthcare",
      },
    ],
    resumeLabel: "Résumé",
    resumeCta: "View Resume",
    resumeHref: "https://bilal-alqtrawi.github.io/BA-Portfolio/Bilal-CV.pdf",
  },
  skills: {
    title: "Skills",
    subtitle: "Grouped by how I work across the stack",
    categories: {
      languages: "Languages",
      frameworks: "Frameworks & libraries",
      databases: "Databases",
      tools: "Tools & delivery",
    },
    items: {
      html: "HTML5",
      css: "CSS3",
      js: "JavaScript (ES6+)",
      ts: "TypeScript",
      sass: "Sass / SCSS",
      sql: "SQL",
      react: "React.js",
      next: "Next.js (v15/16)",
      tailwind: "Tailwind CSS",
      redux: "Redux Toolkit",
      reactQuery: "React Query",
      context: "Context API",
      mui: "Material UI (MUI)",
      framer: "Framer Motion",
      shadcn: "Shadcn/UI",
      swiper: "Swiper.js",
      supabase: "Supabase",
      firebase: "Firebase",
      mysql: "MySQL",
      mongo: "MongoDB",
      git: "Git & GitHub",
      rest: "REST APIs",
      postman: "Postman / Apidog",
      vercel: "Vercel",
      netlify: "Netlify",
      ghActions: "GitHub Actions (CI/CD)",
      dns: "Custom Domains & DNS (SSL)",
      lighthouse: "Lighthouse & Profiling",
      a11y: "Accessibility (WCAG)",
      perf: "Performance (Core Web Vitals)",
      aiTools: "AI-Assisted Development",
    },
  },
  expertise: {
    title: "Expertise",
    subtitle: "What I deliver on client and product work",
    cards: [
      {
        title: "SaaS & dashboards",
        body: "Role-based dashboards, data-heavy UIs, and reusable component libraries with design tokens — built with React, React Query, Redux, and Tailwind or MUI for maintainable scale.",
      },
      {
        title: "E-commerce & marketing sites",
        body: "Product filtering, carts, multi-step checkout, animated heroes, and skeleton loaders — optimized state with Redux, smooth motion with Framer Motion, and reliable API flows.",
      },
      {
        title: "Performance & integration",
        body: "Responsive layouts, Lighthouse-friendly builds, SEO-focused Next.js SSR/SSG, JWT auth patterns, and REST integration deployed on Vercel with cross-browser quality.",
      },
    ],
  },
  experience: {
    eyebrow: "Career",
    title: "Experience & education",
    subtitle:
      "The roles I'm shipping in right now, the work that led here, and the foundation behind my craft.",
    present: "Present",
    current: {
      role: "Front-End & Tech Support Engineer",
      company: "Moawen Platform",
      meta: "Contract · Remote",
      period: "Dec 2025 – Present",
      points: [
        "Architected modular front-end layouts and 10+ RBAC/KYC dashboard modules using React.js, Next.js, RTK, and React Query to secure operational workflows.",
        "Integrated 20+ secure RESTful API endpoints with strict form validation criteria using React Query to streamline multi-step transactional data processing.",
        "Provide ongoing hourly technical support, client-side troubleshooting, and feature maintenance to ensure high system availability and performance.",
      ],
    },
    freelance: {
      role: "Front-End Developer",
      company: "Freelance",
      meta: "Gaza, Palestine",
      period: "2025 – Present",
      points: [
        "Delivered 7+ production web apps (SaaS, dashboards, e-commerce) and engineered 15+ responsive interface templates using Tailwind CSS.",
        "Reduced page load latency by 30%+ through targeted client-side rendering strategies, query caching, and Core Web Vitals optimization.",
        "Leveraged AI-assisted development tools (Cursor, Claude Code, Gemini) to improve feature deployment velocity while maintaining code quality.",
      ],
    },
    education: {
      title: "Education",
      school: "Al-Azhar University – Gaza",
      degree: "B.Sc. in Software Engineering",
      period: "Class of 2025",
      desc: "Focused on web development, OOP, data structures & algorithms, database systems, software testing, cloud computing, and requirements engineering.",
    },
    certifications: {
      title: "Certifications & training",
      items: [
        {
          name: "Top-tech Center",
          label: "Java Programming Course",
          year: "2021",
        },
        {
          name: "Kuwaiti Training Center",
          label: "Full Stack Web Development",
          year: "2021",
        },
        {
          name: "Udemy (Jonas Schmedtmann)",
          label: "The Ultimate React Course 2024",
          year: "2024",
        },
        {
          name: "Elzero Web School",
          label: "Advanced Front-End Track",
          year: "",
        },
      ],
    },
  },
  projects: {
    title: "Selected work",
    subtitle:
      "Production front-ends — e-commerce, SaaS dashboards, healthcare, and corporate sites.",
    moawen: {
      title: "Moawen Platform",
      shortDescription:
        "A multi-role freelance platform connecting clients and freelancers with an advanced admin dashboard.",
      longDescription:
        "Multi-role freelance platform connecting clients and freelancers through a mobile app and advanced admin dashboard. Contributed to building the landing page and dashboard using Next.js, React, MUI, and Tailwind CSS with multilingual support, role permissions, analytics, chats, projects, services, notifications, and dynamic system settings.",
      notes: "💡 Guest account for the control panel: g@g.com | guest@123456",
    },
    masa: {
      title: "Masa Toys Store",
      shortDescription:
        "A full-featured e-commerce platform for a kids' toy store, built with React and Vite.",
      longDescription:
        "A full-featured e-commerce platform for a kids' toy store, built with React and Vite focused on speed and premium UX. Features advanced product filtering and sorting, a dynamic shopping cart, and a multi-step checkout flow powered by Redux Toolkit and React Hook Form. Integrated with seamless Framer Motion animations, Swiper carousels, performance-optimized image lazy loading, and intelligent skeleton loaders connected with backend inventory REST APIs.",
    },
    wsl: {
      title: "WSL Logistics Platform",
      shortDescription:
        "A production-ready landing page and interactive dashboard for managing driver registrations.",
      longDescription:
        "A production-ready landing page and interactive dashboard for managing driver registrations, built with React, Tailwind v4, and Supabase. The live dashboard features dynamic search and filtering, light/dark mode toggling, and direct Excel data exporting.",
      notes:
        "💡 Guest account for the control panel: guest@guest.com | 12345678",
    },
    arsl: {
      title: "Arsl Integrated Healthcare Ecosystem",
      shortDescription:
        "An enterprise-grade Telehealth and EHR platform connecting administration portals, doctors, and patients.",
      longDescription:
        "An enterprise-grade Telehealth and Electronic Health Records (EHR) platform developed within 'Fikratak' team, connecting administration portals, doctor dashboards, and mobile patient apps. Built with Next.js, MUI, and Redux Toolkit under a strict role-based access control (RBAC) system. Features real-time video/voice consultations powered by Stream SDK, live chat via Socket.io, emergency request tracking using interactive maps (Google Maps/Mapbox), and comprehensive modules for pharmacy management and digital wallets. Integrated with Anthropic AI for intelligent clinical assistance, and securely backed by NextAuth, JWT sessions, and Prisma ORM.",
    },
    alghaith: {
      title: "Al-Ghaith & Al-Kharif",
      shortDescription:
        'A comprehensive Next.js website for the law firm "Al-Ghaith & Al-Kharif" with SSR and ISR.',
      longDescription:
        'A comprehensive introductory website for the law firm "Al-Ghaith & Al-Kharif." Built using Next.js and fully reliant on SSR and ISR to ensure optimal SEO and exceptional responsiveness to cases and articles. The interface features a modern and elegant design reflecting a strong legal identity, utilizing Tailwind CSS and Shadcn/UI, with seamless data management via Server Actions to deliver a seamless user experience and the highest performance standards (Core Web Vitals).',
    },
    qira: {
      title: "Qira Platform",
      shortDescription:
        "Developing an interactive Landing Page and optimized open-graph media assets for the Qira platform.",
      longDescription:
        "Engineering the front-end landing page for Qira using Next.js and Tailwind CSS, focusing on customized Open Graph media assets, type-safe development, and optimal user performance.",
    },
    nexvio: {
      title: "NexVio Platform",
      shortDescription:
        "Interactive UI components featuring 3D map views and dynamic theme variables.",
      longDescription:
        "Designing and refactoring interactive UI components and dynamic 3D map displays for NexVio, featuring adaptive layouts and complete light/dark visual theme support.",
    },
    demo: "Live Site",
    code: "Source",
    details: "View details",
    dialog: {
      closeAria: "Close",
    },
    privateRepoNote:
      "This repository is private. Access is restricted to authorized collaborators only.",
  },
  contact: {
    title: "Contact",
    subtitle: "Tell me about your product, timeline, and goals.",
    infoTitle: "Direct",
    formTitle: "Message",
    fullName: "Bilal Alqatrawi",
    phoneValue: "059 309 4894",
    emailValue: "bbill4552@gmail.com",
    name: "Name",
    namePh: "Your name",
    email: "Email",
    emailPh: "you@company.com",
    phone: "Phone / WhatsApp",
    phonePh: "Optional",
    whatsapp: "WhatsApp",
    subject: "Subject",
    subjectPh: "Project / role / collaboration",
    message: "Message",
    messagePh: "What are we building?",
    success: "Opening your mail client…",
    successTitle: "Message sent!",
    successBody:
      "Thanks for reaching out — I'll get back to you as soon as possible.",
    successCta: "Send another message",
    errorRequired: "Email is required.",
    social: "Social",
    location: "Gaza, Palestine",
  },
  footer: {
    rights: "Crafted with care.",
    builtBy: "Bilal Alqatrawi",
    connect: "Stay connected",
  },
  toast: {
    sent: "Thanks — your draft is ready to send.",
    needEmail: "Please add a valid email.",
    errorSubmit: "Something went wrong. Please try again later.",
  },
};

export default en;
